import React from "react";
import { useDispatch, useSelector } from "react-redux";

import useFileSelector from "hooks/useFileSelector";
import { loadCreatives } from "reducers/Creatives";
import {
	exportYoutubeUrlTemplate,
	importYoutubeUrlCreatives,
	pollYoutubeImportJob,
	YOUTUBE_IMPORT_JOB_STATUS
} from "reducers/creatives/YoutubeUrl";
import { addNotification } from "reducers/Notifications";

import { CV } from "utils/constants";
import { hasPermission, toFriendlyErrorMessage } from "utils/common";

import { BasicAction } from "widgets/ActionMenu";

const YOUTUBE_IMPORT_FAILED_MESSAGE = "Import failed. Please try again.";
const YOUTUBE_IMPORT_NO_CREATIVES_MESSAGE = "Import failed. No creatives were created.";
const YOUTUBE_IMPORT_SUCCESS_MESSAGE = "YouTube URL creatives imported.";
const YOUTUBE_IMPORT_STILL_PROCESSING_MESSAGE = "Import is taking longer than expected. We'll notify you when it's done.";
const YOUTUBE_IMPORT_UNKNOWN_ERROR_MESSAGE = "An unknown error has occurred";

export function formatRowError({ request, error }){
	let label = request?.name ? `Row ${request?.row} (${request.name})` : `Row ${request?.row}`;
	return `${label}: ${toFriendlyErrorMessage(error?.description) || YOUTUBE_IMPORT_UNKNOWN_ERROR_MESSAGE}`;
}

function buildImportFailureMessage(job){
	let rowErrors = job.response?.data;

	if(rowErrors?.length){
		return rowErrors.map(formatRowError).map(message => <div key={message}>{message}</div>);
	}

	return toFriendlyErrorMessage(job.response?.description) || YOUTUBE_IMPORT_NO_CREATIVES_MESSAGE;
}

/**
 * Submits a YouTube URL import CSV, then polls the resulting job until it reaches
 * a terminal status, surfacing success/failure feedback and reloading the grid on success.
 */
export function useYoutubeUrlImport(libraryId){
	const dispatch = useDispatch();
	const isImporting = useSelector(state => state.creatives?.isImportingYoutubeUrlCreatives ?? false);

	const notifyImportError = message => dispatch(addNotification({
		message: message || YOUTUBE_IMPORT_FAILED_MESSAGE,
		type: "error",
	}));

	const selectFile = useFileSelector(
		(e) => {
			let file = e.currentTarget.files?.item(0);
			if(!file){ return; }

			let formData = new FormData();
			formData.append("file", file);

			dispatch(importYoutubeUrlCreatives(libraryId, formData)).then((resp) => {
				if(!resp.success){
					notifyImportError(resp.payload.errorObj?.description);
					return;
				}

				dispatch(pollYoutubeImportJob(resp.payload.data.id)).then((pollResp) => {
					if(!pollResp.success){
						notifyImportError(pollResp.payload.errorObj?.description);
						return;
					}

					if(pollResp.timedOut){
						dispatch(addNotification({
							message: YOUTUBE_IMPORT_STILL_PROCESSING_MESSAGE,
							type: "info",
						}));
						return;
					}

					const job = pollResp.payload.data;

					if(job.status === YOUTUBE_IMPORT_JOB_STATUS.FINISHED){
						dispatch(addNotification({
							message: job.feedback || YOUTUBE_IMPORT_SUCCESS_MESSAGE,
							type: "success",
						}));
						dispatch(loadCreatives(libraryId));
						return;
					}

					notifyImportError(buildImportFailureMessage(job));
				});
			});
		},
		{
			accept: ".csv",
		}
	);

	return { selectFile, isImporting };
}

export const EXPORT_YOUTUBE_URL_TEMPLATE = {
	name:"Export YouTube URL Import Template",
	icon:"download",
	bulkAction:true,
	shouldDisplay:({ user }) => hasPermission(user, CV, "Export Versions"),
	component:({ dependentValues }) => {
		const dispatch = useDispatch();
		const { libraryId } = dependentValues;
		return (
			<BasicAction
				name={EXPORT_YOUTUBE_URL_TEMPLATE.name}
				iconName="download"
				handleClick={() => dispatch(exportYoutubeUrlTemplate(libraryId))}
			/>
		);
	},
};

export const IMPORT_YOUTUBE_URL_CREATIVES = {
	name:"Import YouTube URL Creatives",
	icon:"upload",
	bulkAction:true,
	shouldDisplay:({ user }) => hasPermission(user, CV, "Import Versions"),
	component:({ dependentValues }) => {
		const { libraryId } = dependentValues;
		const { selectFile, isImporting } = useYoutubeUrlImport(libraryId);

		return (
			<BasicAction
				name={IMPORT_YOUTUBE_URL_CREATIVES.name}
				iconName="upload"
				isDisabled={isImporting}
				handleClick={selectFile}
			/>
		);
	},
};

export const YOUTUBE_URL_IMPORT_EXPORT_ACTIONS_LIST = [
	IMPORT_YOUTUBE_URL_CREATIVES,
	EXPORT_YOUTUBE_URL_TEMPLATE
];
